import { CalendarEventBuilder } from "./event.builder";
import {
  ProductSharedSettings,
  ProductInventorySettings,
  ProductAdvancedSettings
} from "../product/models";
import { Product } from "../product/product.model";
import * as moment from "moment-timezone";
import { ProductBundleSettings } from "../product/models/product.bundle";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { ToastService } from "ng-uikit-pro-standard";

export class CalendarEvent {
  public readonly id?: string;
  public readonly productId?: string;

  public readonly isCustomEvent: boolean;
  public readonly sold: number;
  public readonly date: Date;
  public readonly timezone: string;
  public readonly rank: number;
  public readonly shared: ProductSharedSettings;
  public readonly inventory: ProductInventorySettings;
  public readonly advanced: ProductAdvancedSettings;
  public readonly bundle: ProductBundleSettings; 

  constructor(builder: CalendarEventBuilder) {
    this.id = builder.id;
    this.productId = builder.productId;
    this.isCustomEvent = builder.isCustomEvent;
    this.sold = builder.sold;
    this.timezone = builder.timezone;
    this.rank = builder.rank;
    this.date = builder.date;
    this.shared = new ProductSharedSettings(builder.shared);
    this.advanced = new ProductAdvancedSettings(builder.advanced);
    this.inventory = new ProductInventorySettings(builder.inventory);
    this.bundle = new ProductBundleSettings(builder.bundle);
  }

  toAPIRequest() {
    let customEvent = {
        date: moment(this.date)
            .tz(this.timezone, true)
            .toDate(),
        inventory: this.inventory.toJSON(),
    } as any

    if (this.shared.description && this.shared.description !== "") {
        customEvent = {
            ...customEvent,
            description: this.shared.description,
        }
    }
    if (this.shared.instructions && this.shared.instructions !== "") {
        customEvent = {
            ...customEvent,
            instructions: this.shared.instructions,
        }
    }
    if (this.shared.warning && this.shared.warning !== "") {
        customEvent = {
            ...customEvent,
            warning: this.shared.warning,
        }
    }
    if (this.shared.questions && this.shared.questions.length > 0) {
        customEvent = {
            ...customEvent,
            questions: this.shared.questions.map(question => question.toJSON()),
        }
    }
    if (this.advanced && !this.checkAdvancedSettingsEmpty(this.advanced)) {
        customEvent = {
            ...customEvent,
            advanced: this.advanced.toJSON(),
        }
    }
    if (this.id) {
        customEvent = {
            ...customEvent,
            rank: this.rank,
            sold: this.sold,
        }
    }

    return customEvent;
  }

  checkAdvancedSettingsEmpty(advancedSettings: ProductAdvancedSettings): boolean {
    if(advancedSettings.purchaseWindow.end !== null ||
        advancedSettings.purchaseWindow.start !== null ||
        advancedSettings.purchaseWindow.timeOnLastDay !== null ||
        advancedSettings.redeemWindow.type !== null ||
        advancedSettings.redeemWindow.dayOfEventBeginTime !== null ||
        advancedSettings.redeemWindow.dayOfEventEndTime !== null ||
        advancedSettings.redeemWindow.daysAfterEvent !== null ||
        advancedSettings.visibleDaysBeforeEvent > -1) {
            return false;
    }
    return true;
  }

  isVisible(product: Product): boolean {
    //? Short Circuit (Return False) If Date is in Past
    if (this.isPastDate()) return false;

    //? Grab Visible Days Object (Check if Event is custom)
    let visibleDays =
      this.advanced.redeemWindow.type == null
        ? product.advanced.visibleDaysBeforeEvent
        : this.advanced.visibleDaysBeforeEvent;
    //? If VisibleDays is negative, Pass is always visible
    if (visibleDays < 0) return true;
    var begin = moment(this.index)
      .tz(this.timezone, false)
      .subtract(visibleDays, "days")
      .add(4, "hours");
    var end = moment(this.index)
      .tz(this.timezone, false)
      .add(1, "day")
      .add(4, "hours");

    return moment.tz(this.timezone).isBetween(begin, end, "D", "[]");
  }

  public get venueTime(): moment.Moment {
    return moment.tz(this.timezone);
  }

  public get isToday(): boolean {
    return this.today.replace("/", "-").replace("/", "-") == this.id;
  }

  public get today(): string {
    return moment.tz(this.timezone).hour() < 4
      ? moment
          .tz(this.timezone)
          .subtract(1, "d")
          .format("MM/DD/YY")
      : moment.tz(this.timezone).format("MM/DD/YY");
  }

  isPastDate(): boolean {
    //? Past Dates are Any Day that is Before the Venues current day (except if its 4am )
    var today = this.today;
    var dateOfEvent = this.index.replace("-", "/").replace("-", "/");
    return moment
      .tz(dateOfEvent, this.timezone)
      .isBefore(moment.tz(today, this.timezone));
  }

  get index(): string {
    return this.id != null
      ? this.id
      : moment(this.date)
          .tz(this.timezone, true)
          .format("MM-DD-YY");
  }
  get label(): string {
    return this.index.replace("-", "/").replace("-", "/");
  }

  async updateRank(
    httpClient: HttpClient,
    userJWT: string,
    rank: number,
  ) {
    return httpClient.post<Product>(
        `${environment.apiBaseUrl}/api/v1/products/${this.productId}/events/${this.id}/rank`,
        {
            productId: this.productId,
            id: this.id,
            rank: rank,
        }, {
            headers: new HttpHeaders().set('Authorization', `${userJWT}`),
        }
    ).toPromise();
  }

  refresh = async (
    httpClient: HttpClient, 
    httpHeaders: HttpHeaders, 
    productId: string,
    toast: ToastService,
  ): Promise<CalendarEvent> => {
    return httpClient.get<Product>(
        `${environment.apiBaseUrl}/api/v1/products/${productId}/events/${this.id}`, 
        {
            headers: httpHeaders,
        }
    ).toPromise()
    .then((eventResponse) => {
        return new CalendarEventBuilder().loadAPIResponse(eventResponse).build();
    })
    .catch((error) => {
        toast.error("Error refreshing event.", "Error", {opacity: 1});
        throw(error);
    });
  }
}
